import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDate } from "@/lib/dateTime";

export const StatusSidebar = ({ appointment, editMode, canEdit, canUpdate, canMaster, formData, setFormData, settings, today, tomorrow }) => {
  const isToday = appointment?.appointment_date === today;
  const isTomorrow = appointment?.appointment_date === tomorrow;
  const isPast = appointment?.appointment_date < today;
  const isFuture = appointment?.appointment_date > tomorrow;
  const isTodayOrTomorrow = isToday || isTomorrow;

  return (
    <Card className="border border-gray-200 rounded-sm shadow-none">
      <CardHeader className="border-b border-gray-100 pb-4">
        <CardTitle className="font-heading font-bold text-lg tracking-tight uppercase">Status</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        {/* --- Row order is stage-specific ---
            Future  : Booking Status → flags
            Tomorrow: N-1 → Booking Status → Docket → flags
            Today   : Day's Outcome → Booking Status → N-1 → Docket → flags
            Past    : Day's Outcome → Booking Status → flags
            Booking Status is the only row that appears on ALL four stages. */}

        {/* N-1 Confirmation — on Tomorrow it sits FIRST (before Booking Status).
            Uses the full outcome-field control (dropdown with reschedule/cancel popups). */}
        {isTomorrow && (
          <OutcomeField
            label="N-1 Confirmation"
            statusField="n_minus_1_confirmation_status"
            appointment={appointment}
            editMode={editMode}
            canEdit={canUpdate}
            formData={formData}
            setFormData={setFormData}
            settings={settings}
            tomorrow={tomorrow}
            testIdPrefix="n1"
          />
        )}

        {/* Day's Outcome — today (update level) and past (master only). */}
        {(isToday || isPast) && (
          <OutcomeField
            label="Day's Outcome"
            statusField="appointment_day_outcome"
            appointment={appointment}
            editMode={editMode}
            canEdit={isPast ? canMaster : canUpdate}
            formData={formData}
            setFormData={setFormData}
            settings={settings}
            tomorrow={tomorrow}
            testIdPrefix="status"
            emptyDisplay="Pending"
          />
        )}

        {/* Booking Status — appears on ALL four stages. Update level;
            past records open only for master. */}
        <OutcomeField
          label="Booking Status"
          statusField="appointment_status"
          appointment={appointment}
          editMode={editMode}
          canEdit={isPast ? canMaster : canUpdate}
          formData={formData}
          setFormData={setFormData}
          settings={settings}
          tomorrow={tomorrow}
          testIdPrefix="booking-status"
        />

        {/* N-1 Confirmation — on Today it sits AFTER Booking Status.
            Uses a lightweight inline Select (no reschedule popup wiring). */}
        {isToday && (
          <div className="flex items-center justify-between">
            <span className="text-sm">N-1 Confirmation</span>
            {editMode && canUpdate ? (
              <Select
                value={formData.n_minus_1_confirmation_status || appointment.n_minus_1_confirmation_status}
                onValueChange={(v) => setFormData({ ...formData, n_minus_1_confirmation_status: v })}
              >
                <SelectTrigger className="rounded-sm w-40" data-testid="n1-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {settings?.n_minus_1_confirmation_statuses?.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Badge className={`rounded-sm text-sm ${appointment.n_minus_1_confirmation_status === "Confirmed" ? "bg-black text-white" : "bg-white text-black border border-dashed border-black"}`}>
                {appointment.n_minus_1_confirmation_status}
              </Badge>
            )}
          </div>
        )}

        {/* Flags */}
        <div className="space-y-3">
          {isTodayOrTomorrow && (
            <FlagRow label="Docket Ready" field="docket_readiness" appointment={appointment} editMode={editMode} canEdit={canUpdate} formData={formData} setFormData={setFormData} />
          )}
          <FlagRow label="OTS/Recall" field="ots" appointment={appointment} editMode={editMode} canEdit={canEdit} formData={formData} setFormData={setFormData} />
          <FlagRow label="Priority" field="priority_customer" appointment={appointment} editMode={editMode} canEdit={canEdit} formData={formData} setFormData={setFormData} />
          <FlagRow label="Lost Customer" field="lost_customer" appointment={appointment} editMode={editMode} canEdit={canEdit} formData={formData} setFormData={setFormData} />
        </div>
      </CardContent>
    </Card>
  );
};

const FlagRow = ({ label, field, appointment, editMode, canEdit, formData, setFormData }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm">{label}</span>
    {editMode && canEdit ? (
      <Switch
        checked={formData[field] ?? appointment[field]}
        onCheckedChange={(v) => setFormData({ ...formData, [field]: v })}
        data-testid={`${field}-toggle`}
      />
    ) : (
      <Badge variant={appointment[field] ? "default" : "outline"} className="rounded-sm text-xs">
        {appointment[field] ? "Yes" : "No"}
      </Badge>
    )}
  </div>
);

/**
 * Reusable outcome field — used for Day Outcome (today) and N-1 Confirmation (tomorrow).
 * Shows dropdown with conditional Reschedule (date + remarks) and Cancel (reason) fields.
 */
const OutcomeField = ({ label, statusField, appointment, editMode, canEdit, formData, setFormData, settings, tomorrow, testIdPrefix, emptyDisplay }) => {
  const rawStatus = formData[statusField] ?? appointment[statusField];
  const currentStatus = rawStatus || emptyDisplay || "";
  // Map each field to its own canonical option list. Previously this was a
  // two-way branch that sent appointment_status to the day-outcomes list.
  let options;
  if (statusField === "appointment_status") {
    options = settings?.appointment_statuses || ["Booked", "Cancelled"];
  } else if (statusField === "appointment_day_outcome") {
    options = settings?.appointment_day_outcomes || ["Pending", "Reported", "Rescheduled", "Cancelled", "No-show"];
  } else {
    options = settings?.n_minus_1_confirmation_statuses || ["Confirmed", "Pending", "Not Reachable", "Rescheduled", "Cancelled"];
  }

  if (editMode && canEdit) {
    return (
      <div>
        <Label className="form-label">{label}</Label>
        <Select
          value={currentStatus}
          onValueChange={(v) => setFormData({ ...formData, [statusField]: v, reschedule_date: formData.reschedule_date || "", reschedule_remarks: formData.reschedule_remarks || "", cancel_reason: formData.cancel_reason || "" })}
        >
          <SelectTrigger className="rounded-sm mt-2" data-testid={`${testIdPrefix}-select`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {currentStatus === "Rescheduled" && (
          <div className="mt-3 space-y-2">
            <Label className="text-xs text-gray-500">Reschedule Date</Label>
            <Input
              type="date"
              min={tomorrow}
              value={formData.reschedule_date || ""}
              onChange={(e) => setFormData({ ...formData, reschedule_date: e.target.value })}
              className="rounded-sm text-sm"
              data-testid={`${testIdPrefix}-reschedule-date`}
            />
            <Label className="text-xs text-gray-500">Remarks</Label>
            <Textarea
              placeholder="Reschedule remarks..."
              value={formData.reschedule_remarks || ""}
              onChange={(e) => setFormData({ ...formData, reschedule_remarks: e.target.value })}
              className="rounded-sm text-sm"
              data-testid={`${testIdPrefix}-reschedule-remarks`}
            />
          </div>
        )}
        {currentStatus === "Cancelled" && (
          <div className="mt-3 space-y-2">
            <Label className="text-xs text-gray-500">Reason for Cancellation</Label>
            <Textarea
              placeholder="Enter reason..."
              value={formData.cancel_reason || ""}
              onChange={(e) => setFormData({ ...formData, cancel_reason: e.target.value })}
              className="rounded-sm text-sm"
              data-testid={`${testIdPrefix}-cancel-reason`}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between">
      <span className="text-sm">{label}</span>
      <div className="text-right">
        <Badge className={`rounded-sm text-sm ${currentStatus === "Confirmed" ? "bg-black text-white" : "bg-white text-black border border-dashed border-black"}`}>
          {currentStatus}
        </Badge>
        {currentStatus === "Rescheduled" && appointment.reschedule_date && (
          <p className="text-xs text-gray-500 mt-1">→ {formatDate(appointment.reschedule_date)}</p>
        )}
        {currentStatus === "Rescheduled" && appointment.reschedule_remarks && (
          <p className="text-xs text-gray-500 mt-1">{appointment.reschedule_remarks}</p>
        )}
        {currentStatus === "Cancelled" && appointment.cancel_reason && (
          <p className="text-xs text-gray-500 mt-1">{appointment.cancel_reason}</p>
        )}
      </div>
    </div>
  );
};
